import {
  CALQUE_OPTION_NAMES,
  parseCalqueOptions,
  resolveCalqueOptions,
} from './lib/options'
import type {
  CalqueEmbedInstance,
  CalqueEmbedOptions,
  CalqueOptions,
} from './lib/types'

interface CalqueEmbedApi {
  embed(container: Element, options: CalqueEmbedOptions): CalqueEmbedInstance
}

declare global {
  interface Window {
    Calque?: Partial<CalqueEmbedApi> & Record<string, unknown>
  }
}

const scriptElement = document.currentScript as HTMLScriptElement | null
const serviceUrl = scriptElement?.src
  ? new URL('./', scriptElement.src)
  : new URL('/', location.href)
const instances = new WeakMap<Element, CalqueEmbedInstance>()

function embed(
  container: Element,
  options: CalqueEmbedOptions = {},
): CalqueEmbedInstance {
  if (!(container instanceof Element)) {
    throw new TypeError('Calque.embed() expects a DOM Element as its first argument.')
  }
  if (!options.post?.trim()) {
    throw new TypeError('Calque.embed() expects a Bluesky post URL or at:// URI.')
  }

  instances.get(container)?.destroy()
  const resolvedOptions = resolveCalqueOptions(options)
  const embedHeight = resolveEmbedHeight(options.height)

  const frameUrl = new URL('embed', serviceUrl)
  for (const name of CALQUE_OPTION_NAMES) {
    setParam(frameUrl, name, resolvedOptions[name])
  }

  const iframe = document.createElement('iframe')
  iframe.src = frameUrl.href
  iframe.title = `${resolvedOptions.title} — Calque`
  iframe.loading = 'lazy'
  iframe.referrerPolicy = 'strict-origin-when-cross-origin'
  iframe.style.cssText = 'border:0;display:block;height:150px;width:100%;'
  iframe.setAttribute('scrolling', 'auto')

  container.replaceChildren(iframe)
  container.setAttribute('data-calque-ready', '')

  let contentHeight = 0
  let animationFrame = 0

  const applyHeight = () => {
    if (!iframe.isConnected) return
    cancelAnimationFrame(animationFrame)

    animationFrame = requestAnimationFrame(() => {
      if (embedHeight === 'content') {
        if (contentHeight) iframe.style.height = `${Math.round(contentHeight)}px`
        return
      }
      if (embedHeight === 'parent') {
        const availableHeight = containerContentHeight(container)
        if (availableHeight > 0) iframe.style.height = `${Math.round(availableHeight)}px`
        return
      }
      iframe.style.height = embedHeight
    })
  }

  const onMessage = (event: MessageEvent) => {
    if (event.origin !== serviceUrl.origin || event.source !== iframe.contentWindow) return
    if (!isResizeMessage(event.data)) return
    contentHeight = Math.min(100_000, Math.max(1, event.data.height))
    applyHeight()
  }

  const containerObserver = new ResizeObserver(applyHeight)
  containerObserver.observe(container)
  const attributeObserver = new MutationObserver(applyHeight)
  attributeObserver.observe(container, {
    attributes: true,
    attributeFilter: ['class', 'style'],
  })
  window.addEventListener('message', onMessage)
  window.addEventListener('resize', applyHeight)
  applyHeight()

  const instance: CalqueEmbedInstance = {
    destroy() {
      cancelAnimationFrame(animationFrame)
      containerObserver.disconnect()
      attributeObserver.disconnect()
      window.removeEventListener('message', onMessage)
      window.removeEventListener('resize', applyHeight)
      iframe.remove()
      container.removeAttribute('data-calque-ready')
      if (instances.get(container) === instance) instances.delete(container)
    },
  }
  instances.set(container, instance)
  return instance
}

function autoEmbed(): void {
  for (const container of document.querySelectorAll('[data-calque]:not([data-calque-ready])')) {
    const options = optionsFromAttributes(container)
    if (!options.post) {
      console.error('A [data-calque] container requires a data-post attribute.', container)
      continue
    }
    embed(container, options)
  }
}

function optionsFromAttributes(container: Element): CalqueEmbedOptions {
  const values: Partial<Record<keyof CalqueOptions, string | null>> = {}
  for (const name of CALQUE_OPTION_NAMES) {
    const attribute = name.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`)
    values[name] = container.getAttribute(`data-${attribute}`)
  }
  return {
    ...parseCalqueOptions(values),
    height: container.getAttribute('data-height')?.trim() || undefined,
  }
}

function containerContentHeight(container: Element): number {
  const style = getComputedStyle(container)
  const padding = Number.parseFloat(style.paddingTop) + Number.parseFloat(style.paddingBottom)
  return Math.max(0, (container as HTMLElement).clientHeight - padding)
}

function resolveEmbedHeight(value: string | undefined): string {
  const height = value?.trim() || 'content'
  if (height === 'content' || height === 'parent') return height
  if (height !== 'auto' && CSS.supports('height', height)) return height
  console.warn(`Invalid Calque iframe height "${height}"; using "content".`)
  return 'content'
}

function isResizeMessage(value: unknown): value is { type: 'calque:resize'; height: number } {
  if (!value || typeof value !== 'object') return false
  const message = value as { type?: unknown; height?: unknown }
  return message.type === 'calque:resize'
    && typeof message.height === 'number'
    && Number.isFinite(message.height)
}

function setParam(url: URL, name: string, value: string | number | undefined): void {
  if (value !== undefined && value !== '') url.searchParams.set(name, String(value))
}

const api = window.Calque ?? {}
api.embed = embed
window.Calque = api

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', autoEmbed, { once: true })
} else {
  queueMicrotask(autoEmbed)
}
